import styled from 'styled-components';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { Tooltip } from '@mui/material';
import { Flex } from '../../Flex';
import { pluralizeString } from '../../../utils/common';
import { ToolInvocationEvent } from '../types';
import { ToolText, formatToolName } from './toolDisplay';

const Chip = styled(Flex)`
  align-items: center;
  gap: 8px;
  width: fit-content;
  max-width: 100%;
`;

const Label = styled(ToolText)`
  white-space: nowrap;
  flex-shrink: 0;
`;

const PreviewText = styled.span`
  color: ${({ theme }) => theme.palette.common.gray12};
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

type Invocation = ToolInvocationEvent['toolInvocation'];

export const stringify = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const getArgsPreview = (
  args?: Record<string, unknown>,
): string | null => {
  if (!args) return null;
  const goal = args.goal;
  if (typeof goal === 'string' && goal.trim()) {
    return goal.trim();
  }
  const entries = Object.entries(args).filter(
    ([, val]) => val !== undefined && val !== null && val !== '',
  );
  if (entries.length === 0) return null;
  const [key, val] = entries[0];
  const valStr = typeof val === 'string' ? val : stringify(val);
  return `${key}: ${valStr}`;
};

export const getRowCount = (invocation: Invocation): number | null => {
  const result = invocation.result?.result;
  if (Array.isArray(result)) return result.length;
  if (result && typeof result === 'object' && Array.isArray(result.rows)) {
    return result.rows.length;
  }
  return null;
};

type Props = {
  invocation: Invocation;
};

const ToolCallChip = ({ invocation }: Props) => {
  const isRunning = invocation.state === 'call';
  const isError = !!invocation.result?.isError;

  const StatusIcon = isError
    ? ErrorRoundedIcon
    : isRunning
    ? SearchRoundedIcon
    : CheckCircleRoundedIcon;

  const label = formatToolName(invocation.toolName);
  const preview = getArgsPreview(invocation.args);
  const rowCount = getRowCount(invocation);

  return (
    <Chip>
      <StatusIcon
        sx={{ fontSize: '18px', color: 'common.magenta', flexShrink: 0 }}
      />
      <Label $running={isRunning}>{label}</Label>
      {preview && (
        <Tooltip title={preview} placement="top-start">
          <PreviewText>{preview}</PreviewText>
        </Tooltip>
      )}
      {rowCount !== null && (
        <PreviewText>· {pluralizeString(rowCount, 'row', 'rows')}</PreviewText>
      )}
    </Chip>
  );
};

export default ToolCallChip;
